import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { tokenManager } from '@/shared/utils/tokenManager';
import { useGlobal } from '@/shared/contexts/GlobalContext';

const EmbedAuthContext = createContext(null);

export function EmbedAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { setTheme, setLang } = useGlobal();
  const { i18n } = useTranslation();

  const handleMessage = useCallback((event) => {
    const { data } = event;

    if (data?.type === 'SET_TOKENS' && data.tokens) {
      tokenManager.setTokens(data.tokens);
      setIsAuthenticated(true);
      setIsLoading(false);
    }

    // Handle SET_TOKEN (singular) — standard EmbedModule format
    if (data?.type === 'SET_TOKEN' && data.token) {
      tokenManager.setTokens({ 'zehn-aip': data.token });
      setIsAuthenticated(true);
      setIsLoading(false);
    }

    // Handle TOKEN_REFRESHED
    if (data?.type === 'TOKEN_REFRESHED' && data.token) {
      tokenManager.setTokens({ 'zehn-aip': data.token });
    }

    // Handle theme changes from parent (zehn-office EmbedModule)
    if (data?.type === 'SET_THEME' || data?.type === 'THEME_CHANGE') {
      const theme = data.payload || data.theme;
      if (theme) {
        setTheme(theme);
      }
    }

    // Handle language changes from parent (zehn-office EmbedModule)
    if (data?.type === 'SET_LANG' || data?.type === 'LANGUAGE_CHANGE') {
      const lang = data.payload || data.lang;
      if (lang) {
        setLang(lang);
        i18n.changeLanguage(lang);
      }
    }
  }, [setTheme, setLang, i18n]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    // Notify parent that iframe is ready
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
    }

    // Also check for tokens passed via URL params (dev mode)
    const params = new URLSearchParams(window.location.search);
    const aipToken = params.get('aip_token');
    if (aipToken) {
      tokenManager.setTokens({ 'zehn-aip': aipToken });
      setIsAuthenticated(true);
    }

    // Timeout — if no tokens received in 5s, mark as not loading
    const timeout = setTimeout(() => setIsLoading(false), 5000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, [handleMessage]);

  return (
    <EmbedAuthContext.Provider value={{ isAuthenticated, isLoading }}>
      {children}
    </EmbedAuthContext.Provider>
  );
}

export function useEmbedAuth() {
  const context = useContext(EmbedAuthContext);
  if (!context) {
    throw new Error('useEmbedAuth must be used within EmbedAuthProvider');
  }
  return context;
}
